import { FormikErrors } from 'formik';

import { FormSection } from '@@/form-components/FormSection';
import { TextTip } from '@@/Tip/TextTip';
import { SliderWithInput } from '@@/form-components/Slider/SliderWithInput';
import { FormControl } from '@@/form-components/FormControl';
import { FormError } from '@@/form-components/FormError';
import { Slider } from '@@/form-components/Slider';

import { ResourceQuotaFormValues } from './types';

type Props = {
  values: ResourceQuotaFormValues;
  onChange: (values: ResourceQuotaFormValues) => void;
  errors: FormikErrors<ResourceQuotaFormValues>;
  namespaceHasQuota: boolean;
  resourceQuotaCapacityExceeded: boolean;
  minMemoryLimit: number;
  minCpuLimit: number;
  maxMemoryLimit: number;
  maxCpuLimit: number;
};

export function ResourceReservationFormSection({
  values,
  onChange,
  errors,
  namespaceHasQuota,
  resourceQuotaCapacityExceeded,
  minMemoryLimit,
  minCpuLimit,
  maxMemoryLimit,
  maxCpuLimit,
}: Props) {
  // Handle memory request change
  function handleMemoryRequestChange(value: number) {
    // If request is greater than limit, adjust limit to match request
    if (value > values.memoryLimit) {
      onChange({ ...values, memoryRequest: value, memoryLimit: value });
    } else {
      onChange({ ...values, memoryRequest: value });
    }
  }

  // Handle memory limit change
  function handleMemoryLimitChange(value: number) {
    // If limit is less than request, adjust request to match limit
    if (value < values.memoryRequest) {
      onChange({ ...values, memoryLimit: value, memoryRequest: value });
    } else {
      onChange({ ...values, memoryLimit: value });
    }
  }

  // Handle CPU request change
  function handleCpuRequestChange(value: number) {
    // If request is greater than limit, adjust limit to match request
    if (value > values.cpuLimit) {
      onChange({ ...values, cpuRequest: value, cpuLimit: value });
    } else {
      onChange({ ...values, cpuRequest: value });
    }
  }

  // Handle CPU limit change
  function handleCpuLimitChange(value: number) {
    // If limit is less than request, adjust request to match limit
    if (value < values.cpuRequest) {
      onChange({ ...values, cpuLimit: value, cpuRequest: value });
    } else {
      onChange({ ...values, cpuLimit: value });
    }
  }

  return (
    <FormSection title="Resource reservations" titleSize="md">
      {!namespaceHasQuota && (
        <TextTip color="blue">
          Resource reservations are applied per instance of the application.
          Resource limits represent the maximum amount of resources that
          containers can use. Containers exceeding these limits may be
          terminated.
        </TextTip>
      )}
      {namespaceHasQuota && !resourceQuotaCapacityExceeded && (
        <TextTip color="blue">
          A resource quota is set on this namespace, you must specify resource
          reservations. Resource reservations are applied per instance of the
          application. Maximums are inherited from the namespace quota.
        </TextTip>
      )}
      <FormControl
        className="flex flex-row"
        label="Memory request (MB)"
        tooltip="An instance of this application will request this amount of memory. This is the minimum amount of memory that will be allocated to the instance."
      >
        <div className="col-xs-10">
          {maxMemoryLimit > 0 && (
            <SliderWithInput
              value={Number(values.memoryRequest) ?? 0}
              onChange={handleMemoryRequestChange}
              min={minMemoryLimit}
              max={maxMemoryLimit}
              step={128}
              dataCy="k8sAppCreate-memoryRequest"
              visibleTooltip
            />
          )}
          {errors?.memoryRequest && (
            <FormError className="pt-1">{errors.memoryRequest}</FormError>
          )}
        </div>
      </FormControl>
      <FormControl
        className="flex flex-row"
        label="Memory limit (MB)"
        tooltip="An instance of this application will reserve this amount of memory. If the instance memory usage exceeds the reservation, it might be subject to OOM."
      >
        <div className="col-xs-10">
          {maxMemoryLimit > 0 && (
            <SliderWithInput
              value={Number(values.memoryLimit) ?? 0}
              onChange={handleMemoryLimitChange}
              min={minMemoryLimit}
              max={maxMemoryLimit}
              step={128}
              dataCy="k8sAppCreate-memoryLimit"
              visibleTooltip
            />
          )}
          {errors?.memoryLimit && (
            <FormError className="pt-1">{errors.memoryLimit}</FormError>
          )}
        </div>
      </FormControl>
      <FormControl
        className="flex flex-row"
        label="CPU request"
        tooltip="An instance of this application will request this amount of CPU. This is the minimum amount of CPU that will be allocated to the instance."
      >
        <div className="col-xs-10">
          {maxCpuLimit > 0 && (
            <Slider
              onChange={(value) =>
                handleCpuRequestChange(
                  typeof value === 'number' ? value : value[0] ?? 0
                )
              }
              value={values.cpuRequest}
              min={minCpuLimit}
              max={maxCpuLimit}
              step={0.1}
              dataCy="k8sAppCreate-cpuRequestSlider"
              visibleTooltip
            />
          )}
          {errors?.cpuRequest && (
            <FormError className="pt-1">{errors.cpuRequest}</FormError>
          )}
        </div>
      </FormControl>
      <FormControl
        className="flex flex-row"
        label="CPU limit"
        tooltip="An instance of this application will reserve this amount of CPU. If the instance CPU usage exceeds the reservation, it might be subject to CPU throttling."
      >
        <div className="col-xs-10">
          {maxCpuLimit > 0 && (
            <Slider
              onChange={(value) =>
                handleCpuLimitChange(
                  typeof value === 'number' ? value : value[0] ?? 0
                )
              }
              value={values.cpuLimit}
              min={minCpuLimit}
              max={maxCpuLimit}
              step={0.1}
              dataCy="k8sAppCreate-cpuLimitSlider"
              visibleTooltip
            />
          )}
          {errors?.cpuLimit && (
            <FormError className="pt-1">{errors.cpuLimit}</FormError>
          )}
        </div>
      </FormControl>
    </FormSection>
  );
}
